import { useEffect, useState } from 'react'
import { cn } from '../../../lib/utils'
import { PHASE_META, PHASE_ORDER, type AlertPhase } from '../../../lib/alert-phase'
import { useUiStore } from '../../../stores/ui'

const timeFmt = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Asia/Shanghai',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
})

const phaseChipClass: Record<AlertPhase, string> = {
  IDLE: 'border-border text-muted-foreground',
  WATCH: 'border-watch/60 text-watch',
  ALERT: 'border-warning/60 text-warning',
  CRITICAL: 'border-critical bg-critical-soft text-critical',
}

const realtimeMeta: Record<string, { label: string; dot: string }> = {
  connected: { label: 'MQTT', dot: 'bg-success' },
  connecting: { label: '连接中', dot: 'bg-warning animate-pulse' },
  degraded: { label: '降级', dot: 'bg-warning' },
  offline: { label: '离线', dot: 'bg-muted-foreground/50' },
}

/**
 * 指挥屏状态条：警报相位机位（IDLE/WATCH/ALERT/CRITICAL）+ UTC+8 时钟 + 链路灯。
 * 相位变化即全局状态切换的可见锚点。
 */
export function StatusStrip({ phase }: { phase: AlertPhase }) {
  const realtimeState = useUiStore((s) => s.realtimeState)
  const [now, setNow] = useState(() => new Date())

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 200)
    return () => clearInterval(timer)
  }, [])

  const rt = realtimeMeta[realtimeState] ?? realtimeMeta.offline

  return (
    <div
      data-phase={phase}
      className="flex items-center gap-3 rounded-md border border-border bg-card px-3 py-1.5"
    >
      <span
        className={cn(
          'text-[11px] font-medium tracking-[0.2em]',
          phase === 'CRITICAL' ? 'text-critical' : 'text-foreground',
        )}
      >
        天穹·镜
      </span>
      <span className="h-3.5 w-px bg-border" aria-hidden="true" />
      <div className="flex items-center gap-1" role="status" aria-label={`系统状态：${PHASE_META[phase].label}`}>
        {PHASE_ORDER.map((p) => (
          <span
            key={p}
            className={cn(
              'rounded border px-1.5 py-px text-[10px] font-mono leading-4 transition-colors',
              p === phase ? phaseChipClass[p] : 'border-border/60 text-muted-foreground/50',
            )}
          >
            {p} · {PHASE_META[p].label}
          </span>
        ))}
      </div>
      <span className="ml-auto flex items-center gap-1.5 text-[10px] text-muted-foreground">
        <span className={cn('inline-block size-1.5 rounded-full', rt.dot)} aria-hidden="true" />
        {rt.label}
      </span>
      <span className="font-mono text-[11px] tabular-nums text-foreground" role="timer" aria-label="北京时间">
        {timeFmt.format(now)}
        <span className="text-muted-foreground">.{String(now.getMilliseconds()).padStart(3, '0')}</span>
      </span>
    </div>
  )
}
